import TableContainer from '@material-ui/core/TableContainer';
import TablePagination from '@material-ui/core/TablePagination';
import { ReactElement } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useApiRequest } from '~/hooks/api';
import Loader from '~/ui/components/common/Loader';
import { getLocalDateNoFormat, normalizeDateString } from '~/utils/date/date';

import ClientAllocationFilters from './ClientAllocationFilters';
import ClientAllocationTable from './ClientAllocationTable';
import useGetAllocations from './useGetAllocations';

import styles from './ClientAllocation.module.scss';

interface IProps {
  clinicId: string;
  actTeamId?: string;
  noActions?: boolean;
  isCrisis?: boolean;
}

const ClientAllocation = ({
  clinicId,
  actTeamId,
  noActions,
  isCrisis = false,
}: IProps): ReactElement => {
  const {
    getAllocations,
    list: clientsAllocationList,
    dateRange,
    currentFilters: {
      filters,
      pagination: { pageSize, pageNumber, total },
    },
  } = useGetAllocations({
    clinicId,
    actTeamId,
    isCrisis,
  });

  const formMethods = useForm({
    defaultValues: {
      sorting: filters.sorting,
      dateRange:
        filters?.dateRange[0] && filters?.dateRange[1]
          ? [getLocalDateNoFormat(filters.dateRange[0]), getLocalDateNoFormat(filters.dateRange[1])]
          : [],
    },
  });
  const { watch } = formMethods;
  const [range, sorting] = watch(['dateRange', 'sorting']);
  const [from, to] = range || [];

  const { isLoading } = useApiRequest(
    () =>
      getAllocations({
        clinicId,
        teamId: actTeamId,
        params: {
          pageSize,
          pageNumber,
          sorting,
          from: from ? normalizeDateString(from) : undefined,
          to: to ? normalizeDateString(to, true) : undefined,
        },
      }),
    {
      enabled: !!from && !!to,
      dependencies: [from, to, sorting],
    },
  );

  const handlePagination = (pSize: number, pNumber: number) =>
    getAllocations({
      clinicId,
      teamId: actTeamId,
      params: {
        pageSize: pSize,
        pageNumber: pNumber,
        sorting: filters.sorting,
        from: dateRange[0] ? normalizeDateString(dateRange[0]) : undefined,
        to: dateRange[1] ? normalizeDateString(dateRange[1], true) : undefined,
      },
    });

  if (isLoading) return <Loader />;

  return (
    <TableContainer className={styles.tableContainer}>
      <div className={styles.filters}>
        <FormProvider {...formMethods}>
          <ClientAllocationFilters />
        </FormProvider>
      </div>
      <ClientAllocationTable
        isCrisis={isCrisis}
        actTeamId={actTeamId}
        noActions={noActions}
        clientsAllocationList={clientsAllocationList}
      />
      <TablePagination
        component="div"
        count={total}
        onPageChange={(_, pNumber) => handlePagination(pageSize, pNumber + 1)}
        onRowsPerPageChange={e => handlePagination(Number(e.target.value), 1)}
        page={pageNumber - 1}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </TableContainer>
  );
};

export default ClientAllocation;
